import React, { useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationCity as BranchIcon,
  Payment as PaymentIcon,
  ShoppingCart as ShoppingCartIcon,
  AccountBalanceWallet as WalletIcon,
  Edit as EditIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { cardHolderAPI } from "../../services/cardHolderAPI";
import { nfcCardAPI } from "../../services/nfcCardAPI";
import rechargeAPI from "../../services/rechargeAPI";
import { saleAPI } from "../../services/saleAPI";
import { transactionAPI } from "../../services/transactionAPI";
import { useRBAC } from "../../hooks/useRBAC";
import NfcCardScanInput from "../../components/nfc/NfcCardScanInput";
import { maskAadhar } from "../../utils/aadhar";
import PageHeader from "../../components/layout/PageHeader";
import HolderAvatar from "../../components/layout/HolderAvatar";
import { tokens } from "../../theme";
import SortableTableCell from "../../components/layout/SortableTableCell";
import { useTableSort } from "../../hooks/useTableSort";
import { sortRows } from "../../utils/sortRows";

const formatCurrency = (value: number | string) =>
  `₹${Number(value || 0).toFixed(2)}`;

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString("en-IN") : "—";

const uiCardNumber = (card: any) => card?.displayNumber || "";

const getCardStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "success";
    case "BLOCKED":
      return "error";
    case "LOST":
      return "warning";
    default:
      return "default";
  }
};

const getRechargeStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "failed":
      return "error";
    default:
      return "default";
  }
};

const getSaleStatusColor = (status: string) => {
  switch (status) {
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  accent: string;
  iconBg: string;
}> = ({ title, value, icon, accent, iconBg }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: tokens.radius.md,
      border: "1px solid",
      borderColor: "divider",
      height: "100%",
      bgcolor: "background.paper",
    }}
  >
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: tokens.radius.sm,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: iconBg,
          color: accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const invalidateHolderQueries = (queryClient: ReturnType<typeof useQueryClient>, hid: string) => {
  queryClient.invalidateQueries({ queryKey: ["cardHolder", hid] });
  queryClient.invalidateQueries({ queryKey: ["holderCards", hid] });
  queryClient.invalidateQueries({ queryKey: ["holderRecharges", hid] });
  queryClient.invalidateQueries({ queryKey: ["holderSales", hid] });
  queryClient.invalidateQueries({ queryKey: ["holderTransactions", hid] });
  queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
};

const HolderProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canPerformRecharge, hasFeature, hasExtraAction } =
    useRBAC();

  const canManageCards =
    canCreate("cardholders") ||
    canUpdate("cardholders") ||
    canCreate("nfc_cards") ||
    canUpdate("nfc_cards");

  const [actionMsg, setActionMsg] = useState<{ type: "success" | "error"; text: string } | null>(
    null
  );

  const [issueOpen, setIssueOpen] = useState(false);
  const [newCardNumber, setNewCardNumber] = useState("");
  const [blockDialog, setBlockDialog] = useState<{
    open: boolean;
    cardId: string;
    cardNumber: string;
    reason: string;
  }>({ open: false, cardId: "", cardNumber: "", reason: "" });
  const [transferOpen, setTransferOpen] = useState(false);
  const [fromCardId, setFromCardId] = useState("");
  const [toCardId, setToCardId] = useState("");
  const [transferAmount, setTransferAmount] = useState("");

  const {
    data: holder,
    isLoading: holderLoading,
    error: holderError,
  } = useQuery({
    queryKey: ["cardHolder", id],
    queryFn: () => cardHolderAPI.getCardHolderById(id!),
    enabled: !!id,
  });

  const { data: cardsData, isLoading: cardsLoading } = useQuery({
    queryKey: ["holderCards", id],
    queryFn: () => nfcCardAPI.getCardsByHolder(id!),
    enabled: !!id,
  });

  const { data: rechargesData, isLoading: rechargesLoading } = useQuery({
    queryKey: ["holderRecharges", id],
    queryFn: () =>
      rechargeAPI.getRecharges({ cardHolderId: id, pageNo: 1, pageSize: 10 }),
    enabled: !!id,
  });

  const { data: salesData, isLoading: salesLoading } = useQuery({
    queryKey: ["holderSales", id],
    queryFn: () =>
      saleAPI.getSales({ cardHolderId: id, pageNo: 1, pageSize: 10 }),
    enabled: !!id,
  });

  const { data: transactionsData, isLoading: transactionsLoading } = useQuery({
    queryKey: ["holderTransactions", id],
    queryFn: () =>
      transactionAPI.getTransactions({ cardHolderId: id, pageNo: 1, pageSize: 10 }),
    enabled: !!id,
  });

  const {
    sortBy: cardSortBy,
    sortOrder: cardSortOrder,
    handleSort: handleCardSort,
  } = useTableSort<"cardNumber" | "balance" | "status">("cardNumber", "asc");
  const {
    sortBy: rechargeSortBy,
    sortOrder: rechargeSortOrder,
    handleSort: handleRechargeSort,
  } = useTableSort<"createdAt" | "amount" | "paymentMethod" | "status">("createdAt", "desc");
  const {
    sortBy: saleSortBy,
    sortOrder: saleSortOrder,
    handleSort: handleSaleSort,
  } = useTableSort<"saleNumber" | "createdAt" | "amount" | "status">("createdAt", "desc");
  const {
    sortBy: txSortBy,
    sortOrder: txSortOrder,
    handleSort: handleTxSort,
  } = useTableSort<
    "createdAt" | "type" | "amount" | "balanceBefore" | "balanceAfter" | "description"
  >("createdAt", "desc");

  const linkInFlightRef = useRef(false);

  const linkCardMutation = useMutation({
    mutationFn: async (cardNumber: string) => {
      if (!id || !holder) throw new Error("Missing holder");
      const num = cardNumber.trim();
      if (!num) throw new Error("Card number required");
      return nfcCardAPI.linkCardToHolder(num, id);
    },
    onSuccess: (card) => {
      invalidateHolderQueries(queryClient, id!);
      queryClient.invalidateQueries({ queryKey: ["nfcCards"] });
      setActionMsg({
        type: "success",
        text: `Card ${uiCardNumber(card)} added to this holder's list.`,
      });
      setIssueOpen(false);
      setNewCardNumber("");
    },
    onError: (e: any) => {
      const msg = e?.message || e?.response?.data?.message || "Could not link card";
      setActionMsg({ type: "error", text: msg });
    },
    onSettled: () => {
      linkInFlightRef.current = false;
    },
  });

  const handleLinkCard = (cardNumber: string) => {
    setNewCardNumber(cardNumber);
    if (linkInFlightRef.current) return;
    linkInFlightRef.current = true;
    linkCardMutation.mutate(cardNumber);
  };

  const transferMutation = useMutation({
    mutationFn: () => {
      const amt = parseFloat(transferAmount);
      if (!fromCardId || !toCardId || fromCardId === toCardId) {
        return Promise.reject(new Error("Select two different cards"));
      }
      if (isNaN(amt) || amt <= 0) {
        return Promise.reject(new Error("Enter a valid amount"));
      }
      return cardHolderAPI.transferBalance(fromCardId, toCardId, amt);
    },
    onSuccess: (data: any) => {
      invalidateHolderQueries(queryClient, id!);
      const amt = data?.amount ?? transferAmount;
      setActionMsg({
        type: "success",
        text: `₹${Number(amt).toFixed(2)} transferred successfully.`,
      });
      setTransferOpen(false);
      setFromCardId("");
      setToCardId("");
      setTransferAmount("");
    },
    onError: (e: any) => {
      setActionMsg({
        type: "error",
        text: e?.message || e?.response?.data?.message || "Transfer failed",
      });
    },
  });

  const updateCardStatusMutation = useMutation({
    mutationFn: ({
      cardId,
      status,
      blockedReason,
    }: {
      cardId: string;
      status: "ACTIVE" | "BLOCKED" | "INACTIVE";
      blockedReason?: string;
    }) =>
      nfcCardAPI.updateCard(cardId, {
        status,
        ...(status === "BLOCKED" && blockedReason ? { blockedReason } : {}),
      }),
    onSuccess: (_, v) => {
      invalidateHolderQueries(queryClient, id!);
      setActionMsg({
        type: "success",
        text: v.status === "BLOCKED" ? "Card blocked." : "Card status updated.",
      });
      setBlockDialog({ open: false, cardId: "", cardNumber: "", reason: "" });
    },
    onError: (e: any) => {
      setActionMsg({
        type: "error",
        text: e?.response?.data?.message || "Could not update card",
      });
    },
  });

  const cards = cardsData?.cards ?? [];

  const sortedCards = useMemo(
    () =>
      sortRows(cards, cardSortBy, cardSortOrder, (row: { balance?: number; status?: string }, field) => {
        if (field === "balance") return Number(row.balance ?? 0);
        if (field === "status") return row.status;
        return uiCardNumber(row);
      }),
    [cards, cardSortBy, cardSortOrder]
  );
  const sortedRecharges = useMemo(
    () =>
      sortRows(
        rechargesData?.data ?? [],
        rechargeSortBy,
        rechargeSortOrder,
        (row: { amount?: number; paymentMethod?: string; status?: string; createdAt?: string }, field) => {
          if (field === "amount") return Number(row.amount ?? 0);
          if (field === "paymentMethod") return row.paymentMethod;
          if (field === "status") return row.status;
          return row.createdAt;
        }
      ),
    [rechargesData?.data, rechargeSortBy, rechargeSortOrder]
  );
  const sortedSales = useMemo(
    () =>
      sortRows(
        salesData?.data ?? [],
        saleSortBy,
        saleSortOrder,
        (
          row: {
            saleNumber?: string;
            orderNumber?: string;
            id?: string;
            netAmount?: number;
            totalAmount?: number;
            status?: string;
            createdAt?: string;
          },
          field
        ) => {
          if (field === "saleNumber") return row.saleNumber || row.orderNumber || row.id;
          if (field === "amount") return Number(row.netAmount ?? row.totalAmount ?? 0);
          if (field === "status") return row.status;
          return row.createdAt;
        }
      ),
    [salesData?.data, saleSortBy, saleSortOrder]
  );
  const sortedTransactions = useMemo(
    () =>
      sortRows(
        transactionsData?.transactions ?? [],
        txSortBy,
        txSortOrder,
        (
          row: {
            type?: string;
            amount?: number;
            balanceBefore?: number;
            balanceAfter?: number;
            description?: string;
            transactionDate?: string;
            createdAt?: string;
          },
          field
        ) => {
          if (field === "type") return row.type;
          if (field === "amount") return Number(row.amount ?? 0);
          if (field === "balanceBefore") return Number(row.balanceBefore ?? 0);
          if (field === "balanceAfter") return Number(row.balanceAfter ?? 0);
          if (field === "description") return row.description ?? "";
          return row.transactionDate || row.createdAt;
        }
      ),
    [transactionsData?.transactions, txSortBy, txSortOrder]
  );

  if (holderLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (holderError || !holder) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/cardholders")} sx={{ mb: 2 }}>
          Back to Holders
        </Button>
        <Alert severity="error">Failed to load holder profile.</Alert>
      </Box>
    );
  }

  const totalBalance = cardsData?.totalBalance ?? cards.reduce(
    (sum: number, card: any) => sum + Number(card.balance || 0),
    0
  );

  const activeCards = cards.filter((c: any) => c.status === "ACTIVE");
  const cardsWithBalance = cards.filter((c: any) => Number(c.balance) > 0);
  const canTransferEligible =
    cards.length >= 2 && cardsWithBalance.length >= 1 && activeCards.length >= 1;
  const canTransfer =
    canTransferEligible && hasFeature("balance_transfer") && canUpdate("cardholders");

  const fromCard = cards.find((c: any) => c.id === fromCardId);
  const maxTransfer = fromCard ? Number(fromCard.balance) : 0;
  const destinationCards = activeCards.filter((c: any) => c.id !== fromCardId);

  const openTransfer = (presetFromId?: string) => {
    const from =
      presetFromId ||
      cardsWithBalance.find((c: any) => c.status === "BLOCKED")?.id ||
      cardsWithBalance[0]?.id ||
      "";
    const to =
      activeCards.find((c: any) => c.id !== from)?.id ||
      activeCards[0]?.id ||
      "";
    setFromCardId(from);
    setToCardId(to);
    setTransferAmount("");
    setTransferOpen(true);
  };

  const sectionCardSx = {
    borderRadius: tokens.radius.md,
    border: "1px solid",
    borderColor: "divider",
    boxShadow: "none",
    bgcolor: "background.paper",
  };

  return (
    <Box>
      <PageHeader
        title={holder.holderName}
        subtitle="Card holder profile, NFC cards, and activity"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Card Holders", to: "/cardholders" },
          { label: holder.holderName },
        ]}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {canManageCards && canUpdate("cardholders") && canTransfer && (
              <Button variant="outlined" size="small" startIcon={<SwapHorizIcon />} onClick={() => openTransfer()}>
                Transfer
              </Button>
            )}
            {canManageCards && canCreate("cardholders") && (
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setIssueOpen(true)}>
                Add card
              </Button>
            )}
            {canPerformRecharge() && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PaymentIcon />}
                onClick={() => navigate("/quick-recharge")}
              >
                Recharge
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/cardholders/${id}/edit`)}
            >
              Edit
            </Button>
          </Stack>
        }
      />

      {actionMsg && (
        <Alert severity={actionMsg.type} sx={{ mb: 2 }} onClose={() => setActionMsg(null)}>
          {actionMsg.text}
        </Alert>
      )}

      {/* Profile summary */}
      <Paper
        elevation={0}
        sx={{
          ...sectionCardSx,
          overflow: "hidden",
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, ${tokens.colors.secondary.main} 0%, ${tokens.colors.secondary.light} 100%)`,
            color: "white",
            px: { xs: 2.5, md: 3 },
            py: { xs: 2.5, md: 3 },
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5} alignItems="center">
            <Box
              sx={{
                p: 0.5,
                borderRadius: tokens.radius.md,
                bgcolor: "rgba(255,255,255,0.15)",
                border: "2px solid rgba(255,255,255,0.35)",
              }}
            >
              <HolderAvatar name={holder.holderName} size={72} />
            </Box>
            <Box flex={1} textAlign={{ xs: "center", sm: "left" }}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {holder.holderName}
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent={{ xs: "center", sm: "flex-start" }}
                mb={1.5}
              >
                <Chip
                  label={holder.isActive ? "Active" : "Inactive"}
                  color={holder.isActive ? "success" : "default"}
                  size="small"
                  sx={{ bgcolor: "white" }}
                />
                {holder.isPrimary && (
                  <Chip label="Primary" color="info" size="small" sx={{ bgcolor: "white" }} />
                )}
                {holder.isBlocked && (
                  <Chip label="Blocked" color="error" size="small" sx={{ bgcolor: "white" }} />
                )}
              </Stack>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                flexWrap="wrap"
                sx={{ opacity: 0.95 }}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneIcon fontSize="small" />
                  <Typography variant="body2">{holder.phone}</Typography>
                </Stack>
                {holder.email && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <EmailIcon fontSize="small" />
                    <Typography variant="body2">{holder.email}</Typography>
                  </Stack>
                )}
                {holder.branch?.name && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <BranchIcon fontSize="small" />
                    <Typography variant="body2">{holder.branch.name}</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Total Balance"
            value={formatCurrency(totalBalance)}
            icon={<WalletIcon fontSize="small" />}
            accent={tokens.colors.dashOrange}
            iconBg="rgba(254, 159, 67, 0.12)"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="NFC Cards"
            value={cards.length}
            icon={<CreditCardIcon fontSize="small" />}
            accent={tokens.colors.dashNavy}
            iconBg="rgba(9, 44, 76, 0.08)"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Recharges"
            value={rechargesData?.total ?? rechargesData?.data?.length ?? 0}
            icon={<PaymentIcon fontSize="small" />}
            accent={tokens.colors.dashTeal}
            iconBg="rgba(40, 199, 111, 0.12)"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Sales"
            value={salesData?.total ?? salesData?.data?.length ?? 0}
            icon={<ShoppingCartIcon fontSize="small" />}
            accent={tokens.colors.dashBlue}
            iconBg="rgba(27, 132, 255, 0.12)"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Profile Details */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Profile details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Full Name</Typography>
                  <Typography fontWeight={600}>{holder.holderName}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Phone</Typography>
                  <Typography fontWeight={600}>{holder.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography fontWeight={600}>{holder.email || "—"}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Aadhar number</Typography>
                  <Typography fontWeight={600} fontFamily="monospace">
                    {maskAadhar(holder.aadharNumber)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Branch</Typography>
                  <Typography fontWeight={600}>{holder.branch?.name || "—"}</Typography>
                </Box>
                <Divider />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  Address
                </Typography>
                <Box>
                  <Typography variant="caption" color="text.secondary">City / Block</Typography>
                  <Typography fontWeight={600}>
                    {[holder.city, holder.block].filter(Boolean).join(", ") || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">District / State</Typography>
                  <Typography fontWeight={600}>
                    {[holder.district, holder.state].filter(Boolean).join(", ") || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Complete address</Typography>
                  <Typography fontWeight={600} sx={{ whiteSpace: "pre-wrap" }}>
                    {holder.address || "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Member Since</Typography>
                  <Typography fontWeight={600}>{formatDate(holder.createdAt)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Holder ID</Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-all", fontFamily: "monospace" }}>
                    {holder.id}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* NFC Cards — right of profile, one row per card */}
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  NFC cards
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {canManageCards && canUpdate("cardholders") && canTransfer && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SwapHorizIcon sx={{ fontSize: 16 }} />}
                      onClick={() => openTransfer()}
                      sx={{ px: 1, py: 0.25, minHeight: 28, fontSize: "0.8125rem" }}
                    >
                      Transfer
                    </Button>
                  )}
                  {canManageCards && canCreate("cardholders") && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setIssueOpen(true)}
                      sx={{ px: 1, py: 0.25, minHeight: 28, fontSize: "0.8125rem" }}
                    >
                      Add card
                    </Button>
                  )}
                  <Button
                    size="small"
                    onClick={() => navigate(`/nfc-cards?holderId=${id}`)}
                    sx={{ px: 1, py: 0.25, minHeight: 28, fontSize: "0.8125rem" }}
                  >
                    Manage Cards
                  </Button>
                </Stack>
              </Stack>

              {cardsLoading ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={28} />
                </Box>
              ) : cards.length === 0 ? (
                <Alert severity="info">
                  No cards assigned yet.
                  {canManageCards && canCreate("cardholders") && (
                    <>
                      {" "}
                      <Button size="small" onClick={() => setIssueOpen(true)}>
                        Issue first card
                      </Button>
                    </>
                  )}
                </Alert>
              ) : (
                <TableContainer>
                  <Table
                    size="small"
                    sx={{
                      "& .MuiTableCell-root": { py: 1, px: 1.5 },
                      "& .MuiTableCell-head": { py: 0.75, fontSize: "0.8125rem" },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <SortableTableCell label="Card number" field="cardNumber" sortBy={cardSortBy} sortOrder={cardSortOrder} onSort={handleCardSort} />
                        <SortableTableCell label="Balance" field="balance" sortBy={cardSortBy} sortOrder={cardSortOrder} onSort={handleCardSort} align="right" />
                        <SortableTableCell label="Status" field="status" sortBy={cardSortBy} sortOrder={cardSortOrder} onSort={handleCardSort} />
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCards.map((card: any) => (
                        <TableRow key={card.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8125rem" }}>
                              {uiCardNumber(card)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontWeight={700} color="primary.main">
                              {formatCurrency(card.balance)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Chip
                                label={card.status}
                                color={getCardStatusColor(card.status)}
                                size="small"
                                sx={{ width: "fit-content" }}
                              />
                              {card.status === "BLOCKED" && card.blockedReason && (
                                <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 200 }}>
                                  {card.blockedReason}
                                </Typography>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              {canPerformRecharge() && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() =>
                                    navigate(
                                      `/quick-recharge?displayNumber=${encodeURIComponent(card.displayNumber || "")}`
                                    )
                                  }
                                  sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: "0.75rem" }}
                                >
                                  Recharge
                                </Button>
                              )}
                              {canManageCards &&
                                hasExtraAction("nfc_cards", "block_card") &&
                                card.status === "ACTIVE" && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<BlockIcon sx={{ fontSize: 14 }} />}
                                  onClick={() =>
                                    setBlockDialog({
                                      open: true,
                                      cardId: card.id,
                                      cardNumber: uiCardNumber(card),
                                      reason: "",
                                    })
                                  }
                                  sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: "0.75rem" }}
                                >
                                  Block
                                </Button>
                              )}
                              {canManageCards &&
                                hasExtraAction("nfc_cards", "unblock_card") &&
                                card.status === "BLOCKED" && (
                                <Button
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                  startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                  onClick={() =>
                                    updateCardStatusMutation.mutate({
                                      cardId: card.id,
                                      status: "ACTIVE",
                                    })
                                  }
                                  sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: "0.75rem" }}
                                >
                                  Activate
                                </Button>
                              )}
                              {canManageCards &&
                                canUpdate("cardholders") &&
                                Number(card.balance) > 0 &&
                                activeCards.some((c: any) => c.id !== card.id) && (
                                  <Button
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    startIcon={<SwapHorizIcon sx={{ fontSize: 14 }} />}
                                    onClick={() => openTransfer(card.id)}
                                    sx={{ px: 1, py: 0.25, minHeight: 26, fontSize: "0.75rem" }}
                                  >
                                    Transfer
                                  </Button>
                                )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Last 10 Recharges */}
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Last 10 recharges
              </Typography>
              {rechargesLoading ? (
                <CircularProgress size={24} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <SortableTableCell label="Date" field="createdAt" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={handleRechargeSort} />
                        <SortableTableCell label="Amount" field="amount" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={handleRechargeSort} />
                        <SortableTableCell label="Method" field="paymentMethod" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={handleRechargeSort} />
                        <SortableTableCell label="Status" field="status" sortBy={rechargeSortBy} sortOrder={rechargeSortOrder} onSort={handleRechargeSort} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedRecharges.map((recharge: any) => (
                        <TableRow key={recharge.id} hover>
                          <TableCell>{formatDate(recharge.createdAt)}</TableCell>
                          <TableCell>{formatCurrency(recharge.amount)}</TableCell>
                          <TableCell>{recharge.paymentMethod}</TableCell>
                          <TableCell>
                            <Chip
                              label={recharge.status}
                              size="small"
                              color={getRechargeStatusColor(recharge.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedRecharges.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No recharges found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Last 10 Sales */}
        <Grid item xs={12} lg={6}>
          <Card elevation={0} sx={{ ...sectionCardSx, height: "100%" }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Last 10 sales
              </Typography>
              {salesLoading ? (
                <CircularProgress size={24} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <SortableTableCell label="Sale #" field="saleNumber" sortBy={saleSortBy} sortOrder={saleSortOrder} onSort={handleSaleSort} />
                        <SortableTableCell label="Date" field="createdAt" sortBy={saleSortBy} sortOrder={saleSortOrder} onSort={handleSaleSort} />
                        <SortableTableCell label="Amount" field="amount" sortBy={saleSortBy} sortOrder={saleSortOrder} onSort={handleSaleSort} />
                        <SortableTableCell label="Status" field="status" sortBy={saleSortBy} sortOrder={saleSortOrder} onSort={handleSaleSort} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedSales.map((sale: any) => (
                        <TableRow key={sale.id} hover>
                          <TableCell>{sale.saleNumber || sale.orderNumber || sale.id.slice(0, 8)}</TableCell>
                          <TableCell>{formatDate(sale.createdAt)}</TableCell>
                          <TableCell>{formatCurrency(sale.netAmount ?? sale.totalAmount)}</TableCell>
                          <TableCell>
                            <Chip
                              label={sale.status}
                              size="small"
                              color={getSaleStatusColor(sale.status)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedSales.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No sales found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Last 10 Transactions */}
        <Grid item xs={12}>
          <Card elevation={0} sx={sectionCardSx}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Last 10 transactions
              </Typography>
              {transactionsLoading ? (
                <CircularProgress size={24} />
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <SortableTableCell label="Date" field="createdAt" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                        <SortableTableCell label="Type" field="type" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                        <SortableTableCell label="Amount" field="amount" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                        <SortableTableCell label="Balance Before" field="balanceBefore" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                        <SortableTableCell label="Balance After" field="balanceAfter" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                        <SortableTableCell label="Description" field="description" sortBy={txSortBy} sortOrder={txSortOrder} onSort={handleTxSort} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedTransactions.map((tx: any) => (
                        <TableRow key={tx.id} hover>
                          <TableCell>{formatDate(tx.transactionDate || tx.createdAt)}</TableCell>
                          <TableCell>
                            <Chip
                              label={tx.type}
                              size="small"
                              color={tx.type === "credit" ? "success" : "error"}
                            />
                          </TableCell>
                          <TableCell>{formatCurrency(tx.amount)}</TableCell>
                          <TableCell>{formatCurrency(tx.balanceBefore)}</TableCell>
                          <TableCell>{formatCurrency(tx.balanceAfter)}</TableCell>
                          <TableCell>{tx.description || "—"}</TableCell>
                        </TableRow>
                      ))}
                      {sortedTransactions.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No transactions found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Issue new card */}
      <Dialog
        open={issueOpen}
        onClose={() => !linkCardMutation.isPending && setIssueOpen(false)}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={linkCardMutation.isPending}
      >
        <DialogTitle>Link card to {holder.holderName}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick a card already registered under <strong>NFC Cards</strong> (no holder). It will appear in this holder&apos;s card list below.
          </Typography>
          <NfcCardScanInput
            value={newCardNumber}
            onChange={setNewCardNumber}
            onScanComplete={handleLinkCard}
            hideScannedValue
            disabled={linkCardMutation.isPending}
            hint="Tap card — links to this holder if unassigned in inventory"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueOpen(false)} disabled={linkCardMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleLinkCard(newCardNumber)}
            disabled={linkCardMutation.isPending || !newCardNumber.trim()}
          >
            {linkCardMutation.isPending ? <CircularProgress size={22} /> : "Link card"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Balance transfer */}
      <Dialog
        open={transferOpen}
        onClose={() => !transferMutation.isPending && setTransferOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Transfer balance between cards</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move money from one card to another for <strong>{holder.holderName}</strong>. Useful when a
            card is blocked but still has balance — transfer to an active card for payments.
          </Typography>

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>From card (source)</InputLabel>
            <Select
              value={fromCardId}
              label="From card (source)"
              onChange={(e) => {
                setFromCardId(e.target.value);
                setToCardId((prev) => (prev === e.target.value ? "" : prev));
                setTransferAmount("");
              }}
            >
              {cardsWithBalance.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {uiCardNumber(c)} — {formatCurrency(c.balance)}
                  {c.status === "BLOCKED" ? " (blocked)" : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" size="small" disabled={!fromCardId}>
            <InputLabel>To card (active)</InputLabel>
            <Select
              value={toCardId}
              label="To card (active)"
              onChange={(e) => setToCardId(e.target.value)}
            >
              {destinationCards.length === 0 ? (
                <MenuItem disabled value="">
                  No active destination card
                </MenuItem>
              ) : (
                destinationCards.map((c: any) => (
                  <MenuItem key={c.id} value={c.id}>
                    {uiCardNumber(c)} — {formatCurrency(c.balance)}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Amount (₹)"
            type="number"
            value={transferAmount}
            onChange={(e) => setTransferAmount(e.target.value)}
            margin="normal"
            inputProps={{ min: 0.01, max: maxTransfer || undefined, step: 0.01 }}
            helperText={
              fromCard
                ? `Available on source card: ${formatCurrency(maxTransfer)}`
                : "Select source card"
            }
          />

          {fromCard && maxTransfer > 0 && (
            <Button
              size="small"
              variant="text"
              onClick={() => setTransferAmount(String(maxTransfer))}
              sx={{ mt: -1 }}
            >
              Transfer full balance ({formatCurrency(maxTransfer)})
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={transferMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<SwapHorizIcon />}
            disabled={
              transferMutation.isPending ||
              !fromCardId ||
              !toCardId ||
              fromCardId === toCardId ||
              !transferAmount ||
              parseFloat(transferAmount) <= 0 ||
              parseFloat(transferAmount) > maxTransfer
            }
            onClick={() => transferMutation.mutate()}
          >
            {transferMutation.isPending ? <CircularProgress size={22} /> : "Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block confirm */}
      <Dialog
        open={blockDialog.open}
        onClose={() =>
          !updateCardStatusMutation.isPending &&
          setBlockDialog({ open: false, cardId: "", cardNumber: "", reason: "" })
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Block card?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Card <strong>{blockDialog.cardNumber}</strong> will be set to BLOCKED. Customer cannot pay with it until
            you activate again.
          </Typography>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            label="Block reason"
            placeholder="e.g. Lost card, fraud suspected, customer request"
            value={blockDialog.reason}
            onChange={(e) =>
              setBlockDialog((prev) => ({ ...prev, reason: e.target.value }))
            }
            disabled={updateCardStatusMutation.isPending}
            inputProps={{ maxLength: 500 }}
            helperText={`${blockDialog.reason.trim().length}/500 — minimum 3 characters`}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setBlockDialog({ open: false, cardId: "", cardNumber: "", reason: "" })
            }
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={
              updateCardStatusMutation.isPending || blockDialog.reason.trim().length < 3
            }
            onClick={() =>
              updateCardStatusMutation.mutate({
                cardId: blockDialog.cardId,
                status: "BLOCKED",
                blockedReason: blockDialog.reason.trim(),
              })
            }
          >
            Block card
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HolderProfilePage;
